import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { HttpService } from '../../../service/http/http.service';
import { environment } from '../../../../environments/environment';
import { backendRestApis } from '../../../../environments/backend-rest-apis';
import { RidingStatistics } from '../../../shared/models/riding-statistics.model';

@Component({
  selector: 'app-riding-statistics',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './riding-statistics.component.html',
  styleUrl: './riding-statistics.component.css'
})
export class RidingStatisticsComponent implements OnInit {

  private httpService = inject(HttpService);
  private baseUrl = `${environment.apiUrl}${backendRestApis.ridingStatistics.getRidingStatistics}`;

  statistics = signal<RidingStatistics | null>(null);
  loading = signal<boolean>(true);
  error = signal<boolean>(false);

  /**
   * Ukupna udaljenost pretvorena iz metara u kilometre, formatirana na 1 decimalu.
   */
  totalDistanceKm = computed<string>(() => {
    const stats = this.statistics();
    return stats ? (stats.totalDistance / 1000).toFixed(1) : '0.0';
  });

  /**
   * Ukupno vrijeme vožnje formatirano kao "Xh YYm".
   */
  totalTimeFormatted = computed<string>(() => {
    const stats = this.statistics();
    return stats ? this.formatDuration(stats.totalTime) : '0h 00m';
  });

  /**
   * Prosječna brzina rekordne rute u km/h, izračunata iz distance/duration.
   */
  recordAverageSpeed = computed<string | null>(() => {
    const route = this.statistics()?.routeWithBiggestAverageSpeed;
    if (!route || !route.duration) {
      return null;
    }
    const speedKmh = (route.distance / route.duration) * 3.6;
    return speedKmh.toFixed(1);
  });

  /**
   * Datum kada je rekordna ruta zabilježena, formatiran za prikaz.
   */
  recordRouteDate = computed<string | null>(() => {
    const route = this.statistics()?.routeWithBiggestAverageSpeed;
    if (!route) {
      return null;
    }
    return new Date(route.startTime).toLocaleDateString('hr-HR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  });

  /**
   * Inicijalizira komponentu i dohvaća agregiranu statistiku vožnji.
   */
  ngOnInit(): void {
    const subscription = this.httpService.get<RidingStatistics>(this.baseUrl)
      .subscribe({
        next: (data) => {
          this.statistics.set(data);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        }
      });

    this.httpService.unsubscribeWithDestroyRef(subscription);
  }

  private formatDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  }
}